import { Icon } from "@material-ui/core";
import { Autocomplete } from "Components/Autocomplete/Autocomplete";
import { AutocompleteEntry } from "Components/Autocomplete/AutocompleteEntry";
import ContentView from "Components/ContentView/ContentView";
import i18n from "i18next";
import { IAreaSelectionUtil } from "Interfaces/Utils/IAreaSelectionUtil";
import { IChipUtil } from "Interfaces/Utils/IChipUtil";
import { resolve } from "inversify-react";
import { Area } from "Models/Areas/Area";
import * as React from "react";
import { connect } from "react-redux";
import { Action, Dispatch } from "redux";
import ServiceProvider from "ServiceProvider/ServiceProvider";
import Types from "ServiceProvider/Types";
import { IAreaState } from "Store/Areas/AreaState";
import { IAreaAction } from "Store/Areas/IAreaAction";
import { IApplicationState } from "Store/Store";
import AreaNavigator from "./AreaNavigator/AreaNavigator";
import { AreaSearch } from "./AreaSearch/AreaSearch";
import styles from "./AreaSelection.scss";

interface IProps {
    areaState: IAreaState;
    selectedArea?: Area;
    showAreaNavigator: boolean;
    className?: string;
    backgroundIsGray?: boolean;
    areaSearchTextboxClassName?: string;
    title: string;
    titleClassName?: string;
    iconClassName?: string;
    getCompanyAreas: () => Action;
    onSelectionChanged(selectedArea?: Area): void;
}

interface IState {
    areas?: Area[];
    selectedArea?: Area;
    filter: string;
    showAutocomplete: boolean;
    autocompleteEntries?: AutocompleteEntry[];
}

const mapDispatchToProps = (dispatch: Dispatch) => ({
    getCompanyAreas: () => {
        const areaAction: IAreaAction = ServiceProvider.get<IAreaAction>(Types.IAreaAction);
        return dispatch(areaAction.getAreasByCompanyId());
    }
});

const mapStateToProps = (state: IApplicationState) => ({
    areaState: state.areaState
});

export class AreaSelection extends React.Component<IProps, IState> {
    @resolve(Types.IAreaSelectionUtil) public areaSelectionUtil: IAreaSelectionUtil;
    @resolve(Types.IChipUtil) public chipUtil: IChipUtil;

    constructor(props: IProps) {
        super(props);

        this.state = {
            showAutocomplete: false,
            areas: [],
            autocompleteEntries: undefined,
            filter: "",
            selectedArea: props.selectedArea
        };

        this.onAreaSelectedHandler = this.onAreaSelectedHandler.bind(this);
        this.onClearFilterHandler = this.onClearFilterHandler.bind(this);
        this.onAreaFilterChangedHandler = this.onAreaFilterChangedHandler.bind(this);
        this.onFocusHandler = this.onFocusHandler.bind(this);
        this.onBlurHandler = this.onBlurHandler.bind(this);
        this.onAutocompleteEntrySelectedHandler = this.onAutocompleteEntrySelectedHandler.bind(this);

        this.props.getCompanyAreas();
    }

    public static getDerivedStateFromProps(nextProps: IProps, prevState: IState): Partial<IState | null> {
        if (nextProps.areaState.companyAreas !== prevState.areas) {
            return {
                areas: nextProps.areaState.companyAreas,
            };
        }
        return null;
    }

    public onFocusHandler() {
        this.setState({ showAutocomplete: true });
    }

    public onBlurHandler() {
        this.setState({ showAutocomplete: false });
    }

    public onAutocompleteEntrySelectedHandler(entry: AutocompleteEntry) {
        this.setState({ showAutocomplete: false });
        this.onAreaSelectedHandler(entry.item);
    }

    public onAreaSelectedHandler(area: Area) {
        this.setState({
            selectedArea: area
        });

        if (this.props.onSelectionChanged) {
            this.props.onSelectionChanged(area);
        }
    }

    public onClearFilterHandler() {
        this.setState({
            selectedArea: undefined
        });

        if (this.props.onSelectionChanged) {
            this.props.onSelectionChanged(undefined);
        }
    }


    public onAreaFilterChangedHandler(filter: string) {
        if (!filter) {
            this.setState({ filter, showAutocomplete: false, autocompleteEntries: undefined });
        }
        else {
            const filteredAreas = this.areaSelectionUtil.getTreeNamesByFilter(filter, this.state.areas!);

            this.setState({
                filter,
                showAutocomplete: true,
                autocompleteEntries: filteredAreas.map(item => {
                    return { key: item.area.id, text: this.chipUtil.getChipString(item.path), item: item.area };
                })
            });
        }
    }

    public render() {
        const selectedArea = (this.state.selectedArea)
            ? this.areaSelectionUtil.findAreaById(this.state.selectedArea.id, this.state.areas!!)
            : undefined;

        return (
            <ContentView className={this.props.className} backgroundIsGray={this.props.backgroundIsGray}>
                <h1 className={styles.title}>
                    <Icon className={this.props.iconClassName}>location_on</Icon>
                    <span className={this.props.titleClassName}>{this.props.title}</span>
                </h1>

                <Autocomplete
                    show={this.state.showAutocomplete}
                    entries={this.state.autocompleteEntries}
                    onBlur={this.onBlurHandler}
                    onFocus={this.onFocusHandler}
                    onEntrySelected={this.onAutocompleteEntrySelectedHandler}
                    noItemsFoundMessage={i18n.t("tasks.area.noAreasFound")}>
                    <AreaSearch
                        value={this.areaSelectionUtil.getAreaRecursiveParents(selectedArea).map(x => x.name)}
                        placeholder={i18n.t("tasks.area.searchPlaceholder")}
                        onClearFilter={this.onClearFilterHandler}
                        onFilterAreas={this.onAreaFilterChangedHandler}
                        textboxClassName={this.props.areaSearchTextboxClassName}
                    />
                </Autocomplete>

                {
                    this.props.showAreaNavigator &&
                    <AreaNavigator
                        areas={this.state.areas}
                        selectedArea={selectedArea}
                        areaSelectedEvent={this.onAreaSelectedHandler}
                        filter="" />
                }
            </ContentView>
        );
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(AreaSelection);


// WEBPACK FOOTER //
// ./src/Components/AreaSelection/AreaSelection.tsx